import type * as Fields from "../types/api-response-fields";

import * as React from "react";

import { getLangFromLocalStorage, getSiteID } from "../utils";
import { usePage } from "./usePage";
import { useSite } from "./useSite";
import { getOptions } from "./utils";

/**
 * Returns an array of the ContentType data from the ReferenceField.
 * This hooks only works in the context of the Griddo editor.
 * @param data The property of the ReferenceField.
 *
 * @example
 * const dataItems = useReferenceFieldData<NewsContentType>(data)
 */
function useReferenceFieldData<ContentType = unknown>(
	/** The ReferenceField from the module schema */
	data?: Fields.Reference<ContentType>,
): Fields.QueriedData<ContentType> | undefined | null {
	const [response, setResponse] = React.useState(null);
	const {
		order,
		quantity,
		sources,
		mode,
		fixed,
		allLanguages,
		preferenceLanguage,
		fullRelations = false,
		site,
		lang: languageFromData,
	} = data || {};

	const { apiUrl, languageId: languageFromPageContext } = usePage();
	const { renderer, siteId: siteIdFromProvider } = useSite();

	// Serialize arrays/objects for stable dependency comparison
	const serializedSources = JSON.stringify(sources);
	const serializedFixed = JSON.stringify(fixed);

	React.useEffect(() => {
		const languageFromLocalStorage = getLangFromLocalStorage();
		const isEditorScope = !!languageFromLocalStorage;
		const languageId =
			languageFromData || languageFromLocalStorage || languageFromPageContext;

		const isSharedPageScope = renderer === "sharedPage";

		if (!isEditorScope && !isSharedPageScope) return;

		const siteID = site || getSiteID() || siteIdFromProvider || "global";
		const url = `${apiUrl}/site/${siteID}/distributor`;

		const body =
			mode === "auto"
				? {
						mode,
						order,
						quantity,
						sources,
						fullRelations,
						allLanguages,
						preferenceLanguage,
					}
				: { mode, fixed, fullRelations };

		const fetchOptions = getOptions(body, {
			lang: languageId,
			authMode: isEditorScope ? "JWT" : "PREVIEW",
		});

		const controller = new AbortController();

		fetch(url, { ...fetchOptions, signal: controller.signal })
			.then((res) => res.json())
			.then((data) => setResponse(data))
			.catch((error) => {
				if (error.name !== "AbortError") {
					console.error("Error:", error);
				}
			});

		return () => controller.abort();
	}, [
		allLanguages,
		apiUrl,
		serializedFixed,
		fullRelations,
		languageFromData,
		mode,
		order,
		preferenceLanguage,
		quantity,
		site,
		serializedSources,
		renderer,
		siteIdFromProvider,
	]);

	return response;
}

export { useReferenceFieldData };
